"use client";

import React, { memo, useMemo } from "react";
import { cn } from "utils";
import { ReportCatalog } from "./ReportCatalog";
import Translations from "@translate/components/CoPilot/360Reports/ReportItem/ReportContent";
import { I18nUtil } from "@utils/i18n";
import { Markdown } from "@/src/components/Markdown";
import type { AgentReportContent } from "@/src/features/api/memberApi";
import { formatMsgQuotes } from "@/src/utils";

const I18n = I18nUtil.createScoped(Translations);

interface ReportContentProps {
  /** 报告内容数据 */
  content?: AgentReportContent;
  /** 自定义类名 */
  className?: string;
}

/**
 * 解析目录数据
 */
const parseCatalog = (toc?: string | null) => {
  if (!toc) {
    return [];
  }

  if (Array.isArray(toc)) {
    return toc
      .map((item, index) => {
        if (typeof item === "string") {
          return { id: `${index}`, title: item };
        }
        if (item && typeof item === "object") {
          return {
            id: (item.id as string) ?? `${index}`,
            title: (item.title as string) ?? `Section ${index + 1}`,
          };
        }
        return null;
      })
      .filter(Boolean) as { id: string; title: string }[];
  }

  try {
    const parsed = JSON.parse(toc);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item, index) => {
          if (typeof item === "string") {
            return { id: `${index}`, title: item };
          }
          if (item && typeof item === "object") {
            return {
              id: (item.id as string) ?? `${index}`,
              title: (item.title as string) ?? `Section ${index + 1}`,
            };
          }
          return null;
        })
        .filter(Boolean) as { id: string; title: string }[];
    }
    if (parsed && typeof parsed === "object") {
      return Object.entries(parsed).map(([key, value]) => ({
        id: key,
        title: typeof value === "string" ? value : `Section ${key}`,
      }));
    }
  } catch (error) {
    // 解析失败时返回空数组
  }
  return [];
};

/**
 * ReportContent 报告内容组件
 * 显示报告的内容目录和正文
 *
 * @param props ReportContentProps
 * @returns React.ReactElement
 */
export const ReportContent: React.FC<ReportContentProps> = memo(
  ({ content, className }) => {
    const catalog = useMemo(() => parseCatalog(content?.toc), [content?.toc]);

    return (
      <div
        className={cn(
          "flex md:flex-row flex-col gap-4 xl: mt-0 mt-1",
          className,
        )}
      >
        {/* 报告目录 */}
        {catalog.length > 0 && (
          <ReportCatalog
            catalog={catalog}
            className="md:max-w-[195px] max-w-full w-full"
          />
        )}
        <div className="flex-1 flex flex-col text-content_primary">
          <h3
            aria-label="Report Content h3"
            data-openreplay-label="Report Content h3 Semantic"
            id="sem-report-content-h3"
            className="title_l text-content_primary"
          >
            {I18n.t("TL;DR")}
          </h3>
          {content?.tldr ? (
            <Markdown>{formatMsgQuotes(content?.tldr)}</Markdown>
          ) : (
            <p className="body_s text-content_tertiary">
              {I18n.t("No additional content available.")}
            </p>
          )}
        </div>
      </div>
    );
  },
);

ReportContent.displayName = "ReportContent";
